/**
 * Supported broadcast event type identifiers.
 *
 * @typedef {'gsc_auth_connection' | 'gbpc_auth_connection'} BroadcastEventType
 * - `'gsc_auth_connection'`  — Fired by the Google Search Console OAuth callback (`Google/Auth.tsx`).
 * - `'gbpc_auth_connection'` — Fired by the Google Business Profile OAuth callback (`GoogleBusiness/Auth.tsx`).
 */
export type BroadcastEventType = 'gsc_auth_connection' | 'gbpc_auth_connection';

/**
 * Callback function signature for handling broadcast events.
 *
 * @callback BroadcastEventHandler
 * @param {any} data - The parsed message payload from the BroadcastChannel.
 */
export type BroadcastEventHandler = (data: any) => void;

/**
 * Represents a registered subscriber within the broadcast service.
 *
 * @interface BroadcastSubscriber
 * @property {string} id - Unique identifier for the subscriber (e.g. `'dashboard'`, `'sidebar-menu'`).
 * @property {Partial<Record<BroadcastEventType, BroadcastEventHandler>>} eventHandlers - Map of event types to their handler callbacks.
 */
interface BroadcastSubscriber {
  id: string;
  eventHandlers: Partial<Record<BroadcastEventType, BroadcastEventHandler>>;
}

/**
 * Central singleton BroadcastChannel manager for cross-tab / cross-window communication.
 *
 * Coordinates and maintains a single, shared `BroadcastChannel('SEO_Space')` instance
 * across the entire application — replacing the previous pattern where every component
 * created its own `new BroadcastChannel('SEO_Space')` with a standalone `onmessage` handler.
 *
 * ### Key Responsibilities:
 * - **Channel Deduplication**: Maintains exactly one `BroadcastChannel` instance, shared by all subscribers.
 * - **Event Demultiplexing**: Dispatches incoming messages (`gsc_auth_connection`, `gbpc_auth_connection`)
 *   to all matching subscriber callbacks via `addEventListener`, avoiding the `onmessage` single-handler overwrite bug.
 * - **Safe Cleanup**: Components unsubscribe on unmount; channel is only closed when the last subscriber leaves.
 * - **Type-Safe API**: Exposes `subscribe`, `unsubscribe`, and `postMessage` with typed event names.
 *
 * ### Usage:
 * ```ts
 * // Subscribe (typically inside useEffect)
 * broadcastService.subscribe('dashboard', {
 *   'gsc_auth_connection': (data) => setGoogleAuth(data)
 * });
 *
 * // Cleanup on unmount
 * return () => broadcastService.unsubscribe('dashboard');
 *
 * // Send a message (from auth callback pages)
 * broadcastService.postMessage({
 *   type: 'gsc_auth_connection',
 *   success: true,
 *   message: 'Connected!'
 * });
 * ```
 */
class BroadcastManager {
  /** The active BroadcastChannel instance, or `null` if no subscribers are registered. */
  private channel: BroadcastChannel | null = null;

  /** Registry of currently active subscribers, mapped by unique subscriber ID. */
  private subscribers = new Map<string, BroadcastSubscriber>();

  /** Bound reference to the message handler so it can be added/removed cleanly. */
  private boundMessageHandler: ((event: MessageEvent) => void) | null = null;

  /**
   * Ensures a single BroadcastChannel instance exists and has the message listener attached.
   *
   * Called automatically by `subscribe` — components should not call this directly.
   *
   * @private
   */
  private ensureChannel(): void {
    if (this.channel) return;

    this.channel = new BroadcastChannel('SEO_Space');
    this.boundMessageHandler = (event: MessageEvent) => {
      this.broadcast(event);
    };
    this.channel.addEventListener('message', this.boundMessageHandler);
  }

  /**
   * Closes the BroadcastChannel and clears the message listener.
   *
   * Called automatically when the last subscriber unsubscribes.
   *
   * @private
   */
  private closeChannel(): void {
    if (this.channel) {
      if (this.boundMessageHandler) {
        this.channel.removeEventListener('message', this.boundMessageHandler);
        this.boundMessageHandler = null;
      }
      this.channel.close();
      this.channel = null;
    }
  }

  /**
   * Registers a subscriber and its event handlers.
   *
   * If the BroadcastChannel is not yet open, it is created automatically.
   * If a subscriber with the same ID already exists, it is replaced.
   *
   * @param {string} id - A unique identifier for the subscriber (e.g. `'dashboard'`, `'gsc-modal'`).
   * @param {Partial<Record<BroadcastEventType, BroadcastEventHandler>>} eventHandlers - Map of event types to handler callbacks.
   *
   * @example
   * ```ts
   * broadcastService.subscribe('site-audit-lists', {
   *   'gsc_auth_connection': (data) => {
   *     setGoogleAuth(data);
   *     setTimeout(() => setGoogleAuth(null), 5000);
   *   }
   * });
   * ```
   */
  public subscribe(
    id: string,
    eventHandlers: Partial<Record<BroadcastEventType, BroadcastEventHandler>>
  ): void {
    this.subscribers.set(id, { id, eventHandlers });
    this.ensureChannel();
  }

  /**
   * Unregisters a subscriber by its unique ID.
   *
   * When all subscribers are removed (subscriber count reaches 0),
   * the underlying BroadcastChannel is closed to free resources.
   *
   * @param {string} id - The unique identifier of the subscriber to remove.
   *
   * @example
   * ```ts
   * // Inside useEffect cleanup
   * return () => broadcastService.unsubscribe('site-audit-lists');
   * ```
   */
  public unsubscribe(id: string): void {
    this.subscribers.delete(id);

    if (this.subscribers.size === 0) {
      this.closeChannel();
    }
  }

  /**
   * Sends a message to all other tabs/windows listening on the `SEO_Space` channel.
   *
   * Automatically ensures the channel exists before posting.
   *
   * @param {{ type: BroadcastEventType; [key: string]: any }} data - The message payload. Must include a `type` field.
   *
   * @example
   * ```ts
   * broadcastService.postMessage({
   *   type: 'gsc_auth_connection',
   *   success: true,
   *   message: 'GSC connected successfully'
   * });
   * ```
   */
  public postMessage(data: { type: BroadcastEventType; [key: string]: any }): void {
    this.ensureChannel();
    this.channel?.postMessage(data);
  }

  /**
   * Dispatches an incoming BroadcastChannel message to all subscribers
   * that have a handler registered for the message's `type`.
   *
   * Safely catches and logs errors in individual subscriber handlers.
   *
   * @private
   * @param {MessageEvent} event - The raw MessageEvent from the BroadcastChannel.
   */
  private broadcast(event: MessageEvent): void {
    const data = event?.data;
    const eventType: BroadcastEventType = data?.type;

    if (!eventType) return;

    this.subscribers.forEach((subscriber) => {
      const handler = subscriber.eventHandlers[eventType];
      if (handler) {
        try {
          handler(data);
        } catch (err) {
          console.error(
            `Error in broadcast handler for '${eventType}' in subscriber '${subscriber.id}':`,
            err
          );
        }
      }
    });
  }

  /**
   * Fully resets the broadcast service by clearing all registered subscribers
   * and closing the channel.
   *
   * Typically called during user logout or full application cleanup.
   */
  public disconnectAll(): void {
    this.subscribers.clear();
    this.closeChannel();
  }
}

/**
 * Shared singleton instance of the BroadcastManager.
 *
 * @type {BroadcastManager}
 */
export const broadcastService: BroadcastManager = new BroadcastManager();
